"use client";

import { useState, useEffect, useRef, memo } from "react";
import { AuthFormPanel } from "./AuthFormPanel";
import { useAuthForm } from "./useAuthForm";

export type AuthMode = "login" | "signup" | "forgot";

type AuthShellProps = {
  mode: "login" | "signup";
};

// ── Cinematic Protected Video Component ──────────────────────────────────────
const ProtectedCinematicVideo = memo(function ProtectedCinematicVideo() {
  return (
    <div className="absolute inset-0 w-full h-full p-6 lg:p-12 flex items-center justify-center overflow-hidden">
      
      {/* The Physical 3D Column Container */}
      <div 
        className="relative w-full max-w-[540px] xl:max-w-[640px] min-h-[600px] h-[76vh] rounded-[3rem] flex flex-col justify-end p-10 pb-8 overflow-hidden z-10"
        style={{
          background: '#c8c6c3', // Matches the video background perfectly
          boxShadow: '0 50px 100px -20px rgba(0,0,0,0.8), inset 0 2px 6px rgba(255,255,255,0.8), inset 0 -2px 10px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255,255,255,0.5)',
        }}
      >
        
        {/* The 3D Cube Video (Optimized Intrinsic Scaling) */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'absolute',
            top: '-2%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '135%',
            maxWidth: 'none', // Overrides Tailwind's default max-w-full on media elements
            height: 'auto', // Shrinks the DOM element to the video's true aspect ratio
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 10,
            // Since the DOM element now tightly wraps the video, this mask PERFECTLY feathers the true bottom edge
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
            maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)'
          }}
        >
          <source src="/rubiks-final.mp4" type="video/mp4" />
        </video>

        {/* Bottom Content - Premium Typographic Lockup (No Containers) */}
        <div className="relative z-20 flex flex-col items-center w-full mt-auto pb-4">
          
          {/* Split the sentence into a dramatic heading and subtitle */}
          <h3 className="text-[24px] font-bold text-neutral-900 text-center max-w-[400px] leading-snug tracking-tight mb-3">
          Every problem you face has a solution waiting here.
          </h3>
          
          <p className="text-[15px] font-medium text-neutral-600 text-center max-w-[340px] leading-relaxed">
          Structured thinking for complex minds.
          </p>
          
        </div>

      </div>
    </div>
  );
});

export function AuthShell({ mode: initialMode }: AuthShellProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const auth = useAuthForm(mode === "signup");

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    const syncModeFromPath = () => {
      const path = window.location.pathname;
      if (path === "/signup") setMode("signup");
      else if (path === "/login") setMode("login");
      else if (path.startsWith("/auth/forgot-password")) setMode("forgot");
    };
    window.addEventListener("popstate", syncModeFromPath);
    return () => window.removeEventListener("popstate", syncModeFromPath);
  }, []);

  const onSwitchMode = (newMode: AuthMode) => {
    setMode(newMode);
    const path = newMode === "forgot" ? "/auth/forgot-password" : `/${newMode}`;
    window.history.pushState(null, "", path);
  };

  return (
    <main className="flex min-h-screen w-full relative bg-[#000000] overflow-hidden text-neutral-900 selection:bg-neutral-900 selection:text-white">
      {/* Premium Global Cinematic Studio Canvas */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#1a1a1f] via-[#09090b] to-[#000000]">
        {/* Faint structural glass grid over the entire canvas */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      {/* LEFT COLUMN - Visual */}
      <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-between z-10">
        <ProtectedCinematicVideo />
      </div>

      {/* RIGHT COLUMN - Form (Premium Dark Mode) */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center p-6 lg:p-12 relative z-10 text-white">
        <div className="relative z-10 w-full max-w-[420px]">
          <AuthFormPanel mode={mode} onSwitchMode={onSwitchMode} {...auth} />
        </div>
      </div>
    </main>
  );
}
